// src/services/apiMonitor.js
// API usage tracking and monitoring for OrderDesk operations

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase.js';

class APIMonitor {
    constructor() {
        this.requestHistory = [];
        this.maxHistorySize = 1000;
        this.operationCounts = new Map();
        this.performanceMetrics = new Map();
        this.alertThresholds = {
            errorRate: 0.15, // 15%
            avgResponseTime: 5000, // 5 seconds
            requestsPerMinute: 50
        };
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // REQUEST TRACKING
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Track API request start
     */
    startRequest(operation, context = {}) {
        const requestId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const request = {
            id: requestId,
            operation,
            method: context.method || 'GET',
            endpoint: context.endpoint,
            startTime: Date.now(),
            timestamp: new Date(),
            
            // Context
            orderId: context.orderId,
            sourceId: context.sourceId,
            userId: context.userId,
            
            // Request details
            requestSize: context.requestData ? JSON.stringify(context.requestData).length : 0,
            
            // Status
            status: 'pending'
        };
        
        this.requestHistory.push(request);
        
        // Trim history if too large
        if (this.requestHistory.length > this.maxHistorySize) {
            this.requestHistory = this.requestHistory.slice(-this.maxHistorySize);
        }
        
        console.log(`[APIMonitor] Started tracking request: ${requestId} (${operation})`);
        
        return requestId;
    }

    /**
     * Track API request completion
     */
    async completeRequest(requestId, result, error = null) {
        try {
            const request = this.requestHistory.find(r => r.id === requestId);
            if (!request) {
                console.warn(`[APIMonitor] Request not found: ${requestId}`);
                return;
            }
            
            const endTime = Date.now();
            const duration = endTime - request.startTime;
            
            // Update request record
            request.endTime = endTime;
            request.duration = duration;
            request.status = error ? 'error' : 'success';
            request.error = error ? {
                message: error.message,
                status: error.status,
                category: error.category
            } : null;
            request.responseSize = result?.data ? JSON.stringify(result.data).length : 0;
            
            // Update operation counts
            const countKey = `${request.operation}_${request.status}`;
            this.operationCounts.set(countKey, (this.operationCounts.get(countKey) || 0) + 1);
            
            // Update performance metrics
            this.updatePerformanceMetrics(request.operation, duration, !error);
            
            // Log to Firestore for long-term storage
            await this.logRequestToFirestore(request);
            
            console.log(`[APIMonitor] Completed tracking request: ${requestId} (${duration}ms, ${request.status})`);
            
            // Check for alerts
            await this.checkAlerts(request);
            
        } catch (logError) {
            console.error(`[APIMonitor] Failed to complete request tracking:`, logError);
        }
    }

    /**
     * Update performance metrics
     */
    updatePerformanceMetrics(operation, duration, success) {
        if (!this.performanceMetrics.has(operation)) {
            this.performanceMetrics.set(operation, {
                totalRequests: 0,
                successfulRequests: 0,
                totalDuration: 0,
                minDuration: Infinity,
                maxDuration: 0,
                avgDuration: 0
            });
        }
        
        const metrics = this.performanceMetrics.get(operation);
        metrics.totalRequests++;
        
        if (success) {
            metrics.successfulRequests++;
        }
        
        metrics.totalDuration += duration;
        metrics.minDuration = Math.min(metrics.minDuration, duration);
        metrics.maxDuration = Math.max(metrics.maxDuration, duration);
        metrics.avgDuration = metrics.totalDuration / metrics.totalRequests;
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // ANALYTICS
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Get API usage statistics
     */
    getUsageStats(timeRange = '1h') {
        const now = Date.now();
        const rangeMs = this.parseTimeRange(timeRange);
        const cutoff = now - rangeMs;
        
        const recentRequests = this.requestHistory.filter(req => 
            req.startTime > cutoff
        );
        
        const stats = {
            timeRange,
            totalRequests: recentRequests.length,
            successfulRequests: recentRequests.filter(r => r.status === 'success').length,
            errorRequests: recentRequests.filter(r => r.status === 'error').length,
            pendingRequests: recentRequests.filter(r => r.status === 'pending').length,
            
            // Performance
            avgResponseTime: 0,
            minResponseTime: Infinity,
            maxResponseTime: 0,
            
            // Operations breakdown
            operations: {},
            
            // Error breakdown
            errors: {}
        };
        
        // Calculate performance metrics
        const completedRequests = recentRequests.filter(r => r.duration !== undefined);
        if (completedRequests.length > 0) {
            const durations = completedRequests.map(r => r.duration);
            stats.avgResponseTime = durations.reduce((a, b) => a + b, 0) / durations.length;
            stats.minResponseTime = Math.min(...durations);
            stats.maxResponseTime = Math.max(...durations);
        }
        
        // Count operations
        recentRequests.forEach(req => {
            stats.operations[req.operation] = (stats.operations[req.operation] || 0) + 1;
            
            if (req.error) {
                const errorKey = req.error.category || 'unknown';
                stats.errors[errorKey] = (stats.errors[errorKey] || 0) + 1;
            }
        });
        
        // Calculate rates
        stats.successRate = stats.totalRequests > 0 ? 
            (stats.successfulRequests / stats.totalRequests) : 1;
        stats.errorRate = stats.totalRequests > 0 ? 
            (stats.errorRequests / stats.totalRequests) : 0;
        stats.requestsPerMinute = stats.totalRequests * (60000 / rangeMs);
        
        return stats;
    }

    /**
     * Get performance metrics for specific operation
     */
    getOperationMetrics(operation) {
        return this.performanceMetrics.get(operation) || {
            totalRequests: 0,
            successfulRequests: 0,
            totalDuration: 0,
            minDuration: 0,
            maxDuration: 0,
            avgDuration: 0
        };
    }

    /**
     * Get top slow operations
     */
    getSlowOperations(limit = 10) {
        return Array.from(this.performanceMetrics.entries())
            .map(([operation, metrics]) => ({
                operation,
                avgDuration: metrics.avgDuration,
                maxDuration: metrics.maxDuration,
                totalRequests: metrics.totalRequests
            }))
            .sort((a, b) => b.avgDuration - a.avgDuration)
            .slice(0, limit);
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // ALERTING
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Check for alert conditions
     */
    async checkAlerts(request) {
        try {
            const stats = this.getUsageStats('5m'); // Check last 5 minutes
            
            // High error rate alert
            if (stats.errorRate > this.alertThresholds.errorRate) {
                await this.triggerAlert('high_error_rate', {
                    errorRate: stats.errorRate,
                    threshold: this.alertThresholds.errorRate,
                    timeRange: '5m',
                    stats
                });
            }
            
            // Slow response time alert
            if (stats.avgResponseTime > this.alertThresholds.avgResponseTime) {
                await this.triggerAlert('slow_response_time', {
                    avgResponseTime: stats.avgResponseTime,
                    threshold: this.alertThresholds.avgResponseTime,
                    timeRange: '5m',
                    stats
                });
            }
            
            // High request rate alert
            if (stats.requestsPerMinute > this.alertThresholds.requestsPerMinute) {
                await this.triggerAlert('high_request_rate', {
                    requestsPerMinute: stats.requestsPerMinute,
                    threshold: this.alertThresholds.requestsPerMinute,
                    timeRange: '5m',
                    stats
                });
            }
            
        } catch (error) {
            console.error(`[APIMonitor] Failed to check alerts:`, error);
        }
    }

    /**
     * Trigger alert
     */
    async triggerAlert(alertType, data) {
        try {
            console.warn(`[APIMonitor] ALERT: ${alertType}`, data);
            
            const alert = {
                type: alertType,
                severity: this.getAlertSeverity(alertType),
                data,
                timestamp: serverTimestamp(),
                resolved: false
            };
            
            await addDoc(collection(db, 'apiAlerts'), alert);
            
            // Could trigger email notifications, Slack alerts, etc.
            
        } catch (error) {
            console.error(`[APIMonitor] Failed to trigger alert:`, error);
        }
    }

    /**
     * Get alert severity
     */
    getAlertSeverity(alertType) {
        const severityMap = {
            'high_error_rate': 'high',
            'slow_response_time': 'medium',
            'high_request_rate': 'medium',
            'api_down': 'critical'
        };
        
        return severityMap[alertType] || 'low';
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // UTILITY METHODS
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Log request to Firestore for long-term storage
     */
    async logRequestToFirestore(request) {
        try {
            const logEntry = {
                ...request,
                timestamp: serverTimestamp(),
                // Remove large data to keep logs manageable
                requestData: request.requestSize > 1000 ? '[Large Request Data]' : request.requestData,
                responseData: request.responseSize > 1000 ? '[Large Response Data]' : request.responseData
            };
            
            await addDoc(collection(db, 'apiRequestLogs'), logEntry);
            
        } catch (error) {
            console.error(`[APIMonitor] Failed to log request to Firestore:`, error);
        }
    }

    /**
     * Parse time range string to milliseconds
     */
    parseTimeRange(timeRange) {
        const match = timeRange.match(/^(\d+)([hmsd])$/);
        if (!match) return 60 * 60 * 1000; // Default to 1 hour
        
        const value = parseInt(match[1], 10);
        const unit = match[2];
        
        switch (unit) {
            case 's': return value * 1000;
            case 'm': return value * 60 * 1000;
            case 'h': return value * 60 * 60 * 1000;
            case 'd': return value * 24 * 60 * 60 * 1000;
            default: return 60 * 60 * 1000;
        }
    }

    /**
     * Export usage data for analysis
     */
    exportUsageData(timeRange = '24h') {
        const stats = this.getUsageStats(timeRange);
        const recentRequests = this.requestHistory.filter(req => 
            req.startTime > (Date.now() - this.parseTimeRange(timeRange))
        );
        
        return {
            summary: stats,
            requests: recentRequests,
            performanceMetrics: Object.fromEntries(this.performanceMetrics),
            exportedAt: new Date().toISOString()
        };
    }

    /**
     * Health check
     */
    async healthCheck() {
        const stats = this.getUsageStats('1h');
        
        return {
            success: true,
            message: 'API monitor operational',
            stats,
            requestHistory: this.requestHistory.length,
            performanceMetrics: this.performanceMetrics.size,
            alerts: {
                errorRateThreshold: this.alertThresholds.errorRate,
                responseTimeThreshold: this.alertThresholds.avgResponseTime,
                requestRateThreshold: this.alertThresholds.requestsPerMinute
            }
        };
    }
}

// Export singleton instance
const apiMonitor = new APIMonitor();
export default apiMonitor;
